//@flow

import React, {useState} from 'react';
import {Box, IconButton, makeStyles, Popover} from '@material-ui/core';
import {
  Delete as DeleteIcon,
  Image as ImageIcon,
  DragIndicator as DragIndicatorIcon,
  Close as CloseIcon,
} from '@material-ui/icons';
import {useProductOptionFormActions} from './ProductOptionFormContext';
import {HexColorPicker} from 'react-colorful';
import {SortableItem, SortableKnob} from 'react-easy-sort';
import CustomTextField from '../../../elements/CustomTextField/CustomTextField';
import {getImageUrl} from '../../../utils/image_provider';
import {colors} from '../../../assets/styles/colors';
import CropModal from '../../../elements/CropModal';
import {TiendadaSwitch} from '../../../elements/tiendada-switch';

import type {OptionFormData} from './ProductOptionFormContext';
import {debounce} from '../../../utils/functions';

const useStyles = makeStyles(theme => ({
  optionItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    background: colors.grey200,
    borderRadius: '12px',
    position: 'relative',
    width: '100%',
    padding: '.75rem',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: '4px',
      padding: '0 .5rem 0 0',
    },
  },
  knob: {
    display: 'none',
    flexShrink: 0,
    height: '32px',
    width: '32px',
    cursor: 'grab',
    backgroundColor: colors.grey300,
    borderRadius: '6px',
    [theme.breakpoints.up('sm')]: {},

    [theme.breakpoints.up('md')]: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: 64,
      width: 'auto',
      backgroundColor: 'transparent',
      borderRadius: 0,
      paddingInline: '8px 4px',
    },
  },
  order: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    fontWeight: 500,
    fontSize: 12,
    backgroundColor: colors.primary,
    color: 'white',
    borderRadius: '50%',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.up('md')]: {
      position: 'static',
      fontSize: 14,
      backgroundColor: 'transparent',
      color: 'inherit',
      borderRadius: 0,
      width: 'auto',
      height: 'auto',
      paddingRight: '.25rem',
    },
  },
  optionFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    flexGrow: 1,

    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      gap: '.5rem',
      alignItems: 'center',
    },
  },
  nameAndPriceContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    flexGrow: 1,
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      alignItems: 'start',
    },
  },
  priceField: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      minWidth: '120px',
      maxWidth: '150px',
      width: 'auto',
    },
  },
  colorSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '0.5rem',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'column',
    },
  },
  colorPreview: {
    height: 40,
    width: 40,
    borderRadius: '4px',
    border: `2px solid ${colors.grey300}`,
    cursor: 'pointer',
    position: 'relative',
  },
  colorPicker: {
    width: '100% !important',
  },
  imageSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '0.5rem',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'column',
    },
  },
  imagePreview: {
    height: 40,
    width: 40,
    borderRadius: '4px',
    border: `2px solid ${colors.grey300}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    backgroundColor: colors.grey200,
    '& img': {
      width: '40px',
      height: '40px',
      maxWidth: '40px',
      maxHeight: '40px',
    },
  },
  removeButton: {
    position: 'absolute',
    top: '-8px',
    right: '-8px',
    backgroundColor: colors.error,
    color: 'white',
    width: '20px',
    height: '20px',
    '&:hover': {
      backgroundColor: colors.error,
    },
  },
  deleteButton: {
    color: colors.danger,
    flexShrink: 0,
    alignSelf: 'flex-end',
    [theme.breakpoints.up('md')]: {
      alignSelf: 'auto',
    },
  },
  hiddenInput: {
    display: 'none',
  },
  textField: {
    flexGrow: 1,
    width: '100%',
    [theme.breakpoints.up('md')]: {
      minWidth: '200px',
      width: 'auto',
    },
  },
  switchSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '0.5rem',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'column',
    },
  },
  switchLabel: {
    fontSize: '0.75rem',
    color: colors.grey600,
    textAlign: 'left',
    [theme.breakpoints.up('md')]: {
      textAlign: 'center',
    },
  },
  mobileActionsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    [theme.breakpoints.up('md')]: {
      display: 'contents',
    },
  },
  mobileFieldsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    [theme.breakpoints.up('md')]: {
      display: 'contents',
    },
  },
  colorImageContainer: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      display: 'contents',
    },
  },
  fieldLabel: {
    fontSize: '0.75rem',
    color: colors.grey600,
    marginBottom: '0.25rem',
    display: 'block',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  colorPickerInput: {
    '& .MuiInputBase-input': {
      fontFamily: 'monospace',
      fontSize: '0.875rem',
      padding: '.5rem !important',
    },
  },
  '@global': {
    '.dragged': {
      transform: 'rotate(8deg)',
      boxShadow: '0px 8px 24px rgba(66, 64, 64, 0.12)',
      backgroundColor: colors.lightPurple,
      border: `1px solid ${colors.primary} !important`,
      zIndex: 1000,
    },
  },
}));

type Props = {
  canDelete: boolean,
  option?: OptionFormData,
};

function ProductOptionItemComponent({canDelete, option}: Props) {
  const classes = useStyles();

  const [colorPickerAnchorEl, setColorPickerAnchorEl] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const {
    removeOption,
    updateOptionName,
    updateOptionPrice,
    updateOptionColor,
    updateOptionEnabled,
    updateOptionImageFile,
    removeOptionColor,
    removeOptionImage,
  } = useProductOptionFormActions();

  const optionErrors = {};

  const existingImageUrl = option?.imageId ? getImageUrl(option.imageId) : null;

  const colorPickerOpen = Boolean(colorPickerAnchorEl);

  const handleNameChange = (event: any) => {
    updateOptionName(option.id, event.target.value);
  };

  const handlePriceChange = (event: any) => {
    const value = parseFloat(event.target.value) || 0;
    updateOptionPrice(option.id, value);
  };

  const handleColorChange = debounce((color: string) => {
    updateOptionColor(option.id, color);
  }, 300);

  const handleRemoveColor = () => {
    removeOptionColor(option.id);
  };

  const handleColorPickerClick = event => {
    setColorPickerAnchorEl(event.currentTarget);
  };

  const handleChangeOptionEnabled = (enabled: boolean) => {
    updateOptionEnabled(option.id, enabled);
  };

  const handleColorPickerClose = () => {
    setColorPickerAnchorEl(null);
  };

  const handleImageChange = (event: any) => {
    const target = event.target;
    if (target && target.files && target.files.length > 0) {
      const file = target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          setSelectedFile({
            file,
            preview: e.target.result,
            aspect: 1,
          });
          setCropModalOpen(true);
        };
        reader.readAsDataURL(file);
      }
    }
    target.value = '';
  };

  const handleCropComplete = croppedImages => {
    if (croppedImages.length > 0) {
      const result = croppedImages[0];
      const croppedFile = result.file;
      updateOptionImageFile(option.id, croppedFile);
      setImagePreview(result.preview);
    }
    setCropModalOpen(false);
    setSelectedFile(null);
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    setSelectedFile(null);
  };

  const handleRemoveImage = () => {
    removeOptionImage(option.id);
  };

  const handleRemoveOption = () => {
    removeOption(option.id);
  };

  return (
    <SortableItem key={option.id}>
      <div className={classes.optionItem}>
        <SortableKnob>
          <span className={classes.knob}>
            <DragIndicatorIcon />
          </span>
        </SortableKnob>

        <div className={classes.optionFields}>
          <div className={classes.mobileFieldsContainer}>
            <div className={classes.nameAndPriceContainer}>
              <CustomTextField
                className={classes.textField}
                onChange={handleNameChange}
                value={option?.name || ''}
                label="Nombre de la opción"
                size="small"
                fullWidth
                error={!!optionErrors?.name}
                helperText={optionErrors?.name}
              />

              <CustomTextField
                className={classes.priceField}
                onChange={handlePriceChange}
                value={option?.price || 0}
                label="Precio"
                size="small"
                type="number"
                inputProps={{
                  min: 0,
                  step: 0.01,
                }}
                error={!!optionErrors?.price}
                helperText={optionErrors?.price}
              />
            </div>

            <div className={classes.colorImageContainer}>
              <Box className={classes.colorSection}>
                <span className={classes.fieldLabel}>Color</span>
                <Box position="relative">
                  <Box
                    className={classes.colorPreview}
                    style={{
                      backgroundColor: option?.color || '#ffffff',
                    }}
                    onClick={handleColorPickerClick}
                  />
                  {option?.color && (
                    <IconButton
                      className={classes.removeButton}
                      size="small"
                      onClick={handleRemoveColor}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                  <Popover
                    open={colorPickerOpen}
                    anchorEl={colorPickerAnchorEl}
                    onClose={handleColorPickerClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <Box padding={2} borderRadius={4}>
                      <HexColorPicker
                        color={option?.color || '#ffffff'}
                        onChange={handleColorChange}
                        className={classes.colorPicker}
                      />
                      <Box
                        display="flex"
                        alignItems="center"
                        style={{
                          gap: '0.5rem',
                        }}
                        mt={2}
                      >
                        <div
                          style={{
                            backgroundColor: option?.color || '#ffffff',
                            width: '32px',
                            height: '32px',
                            borderRadius: '.25rem',
                            flexShrink: 0,
                            border: `1px solid ${colors.grey300}`,
                          }}
                        />
                        <CustomTextField
                          value={option?.color || ''}
                          onChange={handleRemoveColor}
                          className={classes.colorPickerInput}
                          size="small"
                        />
                      </Box>
                    </Box>
                  </Popover>
                </Box>
              </Box>

              <Box className={classes.imageSection}>
                <span className={classes.fieldLabel}>Imagen</span>
                <label>
                  <input
                    type="file"
                    accept="image/*"
                    className={classes.hiddenInput}
                    onChange={handleImageChange}
                  />
                  <Box className={classes.imagePreview} position="relative">
                    {imagePreview || existingImageUrl ? (
                      <>
                        <img
                          src={imagePreview || existingImageUrl}
                          alt="Preview"
                        />
                        <IconButton
                          className={classes.removeButton}
                          size="small"
                          onClick={handleRemoveImage}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <ImageIcon fontSize="small" color="action" />
                    )}
                  </Box>
                </label>
              </Box>
            </div>
          </div>

          <div className={classes.mobileActionsContainer}>
            <Box className={classes.switchSection}>
              <TiendadaSwitch
                checked={!!option?.enabled}
                onChange={e => handleChangeOptionEnabled(e.target.checked)}
                color="primary"
                size="small"
                label="Habilitado"
              />
            </Box>

            {canDelete && (
              <IconButton
                className={classes.deleteButton}
                onClick={handleRemoveOption}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </div>
        </div>

        {selectedFile && (
          <CropModal
            open={cropModalOpen}
            onClose={handleCropCancel}
            images={[selectedFile]}
            compressOptions={{
              quality: 1,
              maxHeight: 800,
              maxWidth: 800,
            }}
            onSubmit={handleCropComplete}
            aspect={1}
          />
        )}
      </div>
    </SortableItem>
  );
}

const ProductOptionItem = React.memo(ProductOptionItemComponent);

export default ProductOptionItem;
